import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Settings, Bell, Shield, Database, Palette, X, Save } from 'lucide-react';
import { toast } from 'react-toastify';

const SettingsAction = () => {
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState({
    notifications: {
      emailAlerts: true,
      systemUpdates: true,
      userActivity: false,
      errorReports: true,
    },
    security: {
      twoFactorAuth: false,
      sessionTimeout: 30,
      ipWhitelist: false,
      auditLogging: true,
      clientOtpEnabled: false,
    },
    appearance: {
      theme: 'light',
      compactMode: false,
      showAnimations: true,
      language: 'en',
    },
    system: {
      autoBackup: true,
      backupFrequency: 'daily',
      logRetention: 90,
      performanceMonitoring: true,
    },
  });

  const tabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'system', label: 'System', icon: Database },
  ];

  const handleSettingChange = (category, key, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value,
      },
    }));
  };

  const handleSaveSettings = async () => {
    try {
      // Save settings to Firestore for admin synchronization
      const { doc, setDoc } = await import('firebase/firestore');
      const { firestore } = await import('../../../database/firebase');

      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const adminSettingsRef = doc(firestore, 'adminSettings', currentUser.uid || 'default');

      await setDoc(
        adminSettingsRef,
        {
          ...settings,
          lastModified: new Date(),
          modifiedBy: currentUser.email || 'unknown',
          adminId: currentUser.uid || 'unknown',
        },
        { merge: true }
      );

      // Persist global security settings used by authentication flows (e.g. client OTP)
      const securitySettingsRef = doc(firestore, 'systemSettings', 'security');
      await setDoc(
        securitySettingsRef,
        {
          clientOtpEnabled: !!settings.security.clientOtpEnabled,
          // room for future global auth flags without touching per-admin preferences
          updatedAt: new Date(),
          updatedBy: currentUser.email || 'unknown',
        },
        { merge: true }
      );

      toast.success('Settings saved and synchronized with other admins');
      setShowModal(false);
    } catch (error) {
      toast.error('Failed to save settings: ' + error.message);
    }
  };

  const renderSettingsContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Dashboard Refresh Rate
              </label>
              <select
                value={settings.system?.refreshRate || '30'}
                onChange={e => handleSettingChange('system', 'refreshRate', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              >
                <option value="15">15 seconds</option>
                <option value="30">30 seconds</option>
                <option value="60">1 minute</option>
                <option value="300">5 minutes</option>
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Default View</label>
              <select
                value={settings.general?.defaultView || 'overview'}
                onChange={e => handleSettingChange('general', 'defaultView', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              >
                <option value="overview">Overview</option>
                <option value="analytics">Analytics</option>
                <option value="cohorts">Cohorts</option>
                <option value="alerts">Alerts</option>
              </select>
            </div>
          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-4">
            {Object.entries(settings.notifications).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium capitalize text-gray-700">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </label>
                  <p className="text-xs text-gray-500">
                    {key === 'emailAlerts' && 'Receive email notifications for important events'}
                    {key === 'systemUpdates' && 'Get notified about system updates and maintenance'}
                    {key === 'userActivity' && 'Monitor user activity and engagement'}
                    {key === 'errorReports' && 'Receive alerts for system errors and issues'}
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={value}
                  onChange={e => handleSettingChange('notifications', key, e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </div>
            ))}
          </div>
        );

      case 'security':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Two-Factor Authentication
                </label>
                <p className="text-xs text-gray-500">
                  Admin 2FA for protecting privileged access. This does not affect client logins.
                </p>
              </div>
              <input
                type="checkbox"
                checked={settings.security.twoFactorAuth}
                onChange={e => handleSettingChange('security', 'twoFactorAuth', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            </div>

            <div className="rounded-lg border border-blue-100 bg-blue-50 p-3">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-semibold text-gray-800">Client OTP (Login)</label>
                  <p className="text-xs text-gray-600">
                    Require a one-time password for{' '}
                    <span className="font-semibold">client / entrepreneur logins only</span>. Admins
                    continue to use the dedicated 2FA system.
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <input
                    type="checkbox"
                    checked={settings.security.clientOtpEnabled}
                    onChange={e =>
                      handleSettingChange('security', 'clientOtpEnabled', e.target.checked)
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                      settings.security.clientOtpEnabled
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {settings.security.clientOtpEnabled ? 'Enabled for clients' : 'Disabled'}
                  </span>
                </div>
              </div>
              <p className="mt-2 text-[11px] text-gray-500">
                OTP is a separate control from Admin 2FA. Turning this on affects only client /
                entrepreneur login flows and does not change how admins authenticate.
              </p>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Session Timeout (minutes)
              </label>
              <input
                type="number"
                value={settings.security.sessionTimeout}
                onChange={e =>
                  handleSettingChange('security', 'sessionTimeout', parseInt(e.target.value))
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                min="5"
                max="480"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Audit Logging</label>
                <p className="text-xs text-gray-500">Log all administrative actions for security</p>
              </div>
              <input
                type="checkbox"
                checked={settings.security.auditLogging}
                onChange={e => handleSettingChange('security', 'auditLogging', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            </div>
          </div>
        );

      case 'appearance':
        return (
          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Theme</label>
              <div className="flex gap-3">
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="radio"
                    name="theme"
                    value="light"
                    checked={settings.appearance.theme === 'light'}
                    onChange={e => handleSettingChange('appearance', 'theme', e.target.value)}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Light</span>
                </label>
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="radio"
                    name="theme"
                    value="dark"
                    checked={settings.appearance.theme === 'dark'}
                    onChange={e => handleSettingChange('appearance', 'theme', e.target.value)}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Dark</span>
                </label>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Compact Mode</label>
                <p className="text-xs text-gray-500">Reduce spacing for more content</p>
              </div>
              <input
                type="checkbox"
                checked={settings.appearance.compactMode}
                onChange={e => handleSettingChange('appearance', 'compactMode', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Show Animations</label>
                <p className="text-xs text-gray-500">Enable smooth transitions and animations</p>
              </div>
              <input
                type="checkbox"
                checked={settings.appearance.showAnimations}
                onChange={e =>
                  handleSettingChange('appearance', 'showAnimations', e.target.checked)
                }
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            </div>
          </div>
        );

      case 'system':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Auto Backup</label>
                <p className="text-xs text-gray-500">Automatically backup system data</p>
              </div>
              <input
                type="checkbox"
                checked={settings.system.autoBackup}
                onChange={e => handleSettingChange('system', 'autoBackup', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Backup Frequency
              </label>
              <select
                value={settings.system.backupFrequency}
                onChange={e => handleSettingChange('system', 'backupFrequency', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              >
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Log Retention (days)
              </label>
              <input
                type="number"
                value={settings.system.logRetention}
                onChange={e =>
                  handleSettingChange('system', 'logRetention', parseInt(e.target.value))
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                min="7"
                max="365"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setShowModal(true)}
        className="flex flex-col items-center rounded-lg bg-gradient-to-br from-purple-50 via-purple-100 to-purple-200 p-3 shadow-md transition-colors hover:bg-purple-200 md:p-4"
      >
        <span className="mb-1.5 md:mb-2">
          <Settings className="h-5 w-5 text-purple-700 md:h-6 md:w-6" />
        </span>
        <span className="text-xs font-medium text-purple-900 md:text-sm">Settings</span>
      </motion.button>

      {/* Settings Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-h-[90vh] w-full max-w-4xl rounded-xl bg-white shadow-2xl"
          >
            <div className="border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-purple-100 p-2">
                    <Settings className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">Admin Settings</h3>
                    <p className="text-sm text-gray-600">
                      Configure system preferences and security
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="flex h-96">
              {/* Settings Sidebar */}
              <div className="w-64 border-r border-gray-200 bg-gray-50 p-4">
                <nav className="space-y-1">
                  {tabs.map(tab => {
                    const IconComponent = tab.icon;
                    const isActive = activeTab === tab.id;

                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-purple-100 text-purple-700'
                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                      >
                        <IconComponent className="h-4 w-4" />
                        {tab.label}
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Settings Content */}
              <div className="flex-1 p-6">
                <div className="h-full overflow-y-auto">{renderSettingsContent()}</div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => setShowModal(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSettings}
                className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700"
              >
                <Save className="h-4 w-4" />
                Save Settings
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default SettingsAction;
